import { AfterViewInit, Directive, Input, OnDestroy } from '@angular/core';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSortable, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription, interval } from 'rxjs';

@Directive({
  selector: '[appNgMatTableQueryReflector]',
})
export class NgMatTableQueryReflectorDirective implements AfterViewInit, OnDestroy {
  @Input() dataSource: MatTableDataSource<any>;
  @Input() paginator: MatPaginator;
  @Input() filter: HTMLInputElement;

  subscription: Subscription;
  pageSubscription: Subscription;
  sortSubscription: Subscription;

  constructor(
    private _router: Router,
    private _activatedRoute: ActivatedRoute
  ) {}

  ngOnDestroy(): void {
    if (this.subscription) this.subscription.unsubscribe();
    if (this.pageSubscription) this.pageSubscription.unsubscribe();
    if (this.sortSubscription) this.sortSubscription.unsubscribe();
  }

  ngAfterViewInit(): void {
    this.initialSetup();
    this.listenToStateChangeEvents();
  }

  private async initialSetup(): Promise<void> {
    // HACK 1
    await this.waitForQueryParamsToLoad();

    const activePageQuery = this.isPageQueryActive();

    if (activePageQuery) {
      this.paginator.pageIndex = activePageQuery.page_index;
      this.paginator.pageSize = activePageQuery.page_size;
    }

    const activeFilterQuery = this.isFilterQueryActive();

    if (activeFilterQuery && this.filter)
    {
      this.filter.value = activeFilterQuery.filter;
    }

    const activeSortQuery = this.activeSortQuery;

    if (activeSortQuery)
    {
      const sortable: MatSortable = {
        id: activeSortQuery.sort_active,
        start: activeSortQuery.sort_direction,
        disableClear: true,
      };

      this.dataSource.sort.sort(sortable);

      // HACK 2
      const activeSortHeader = this.dataSource.sort.sortables.get(
        activeSortQuery.sort_active
      );
      activeSortHeader['_setAnimationTransitionState']({
        fromState: this.dataSource.sort.direction,
        toState: 'active',
      });
    }

  }

  private get activeSortQuery(): {
    sort_active: string;
    sort_direction: 'asc' | 'desc';
  } {
    const queryParams = this._activatedRoute.snapshot.queryParams;
    if (
      queryParams.hasOwnProperty('sort_active') &&
      queryParams.hasOwnProperty('sort_direction')
    ) {
      return {
        sort_active: queryParams['sort_active'],
        sort_direction: queryParams['sort_direction'],
      };
    }
    return null;
  }

  private waitForQueryParamsToLoad(): Promise<void> {
    if (!window.location.search) {
      return null;
    }

    const titleCheckingInterval$ = interval(500);

    return new Promise((resolve) => {
      this.subscription = titleCheckingInterval$.subscribe((val) => {
        if (
          Object.values(this._activatedRoute.snapshot.queryParams).length > 0
        ) {
          return resolve();
        }
      });
    });
  }

  private listenToStateChangeEvents(): void {
    this.pageSubscription = this.paginator.page.subscribe((pageChange: PageEvent) => {
      this._applyPageStateChangesToUrlQueryParams(pageChange);
    });

    this.sortSubscription = this.dataSource.sort.sortChange.subscribe((sortChange: Sort) => {
      this._applySortChangesToUrlQueryParams(sortChange);
    });


    if (this.filter)
    {
      this.filter.addEventListener('keyup', (event) => {
        this._applyFilterChangesToUrlQueryParams(this.filter.value);
      });
    }
  }
  private _applySortChangesToUrlQueryParams(sortChange: Sort): void {
    const sortingAndPaginationQueryParams = {
      sort_active: sortChange.active,
      sort_direction: sortChange.direction,
    };
    this._router.navigate([], {
      queryParams: sortingAndPaginationQueryParams,
      queryParamsHandling: 'merge',
    });
  }

  private _applyPageStateChangesToUrlQueryParams(pageChange: PageEvent): void {
    const sortingAndPaginationQueryParams = {
      page_size: pageChange.pageSize,
      page_index: pageChange.pageIndex,
    };

    this._router.navigate([], {
      queryParams: sortingAndPaginationQueryParams,
      queryParamsHandling: 'merge',
    });
  }

  private _applyFilterChangesToUrlQueryParams(value: String): void {
    const filterQueryParams = {
      filter: value,
    };

    this._router.navigate([], {
      queryParams: filterQueryParams,
      queryParamsHandling: 'merge',
    });
  }


  private isPageQueryActive(): { page_size: number; page_index: number } {
    const queryParams = this._activatedRoute.snapshot.queryParams;

    if (
      queryParams.hasOwnProperty('page_size') ||
      queryParams.hasOwnProperty('page_index')
    ) {
      return {
        page_size: queryParams['page_size'],
        page_index: queryParams['page_index'],
      };
    }

    return null;
  }

  private isFilterQueryActive(): { filter: string } {
    const queryParams = this._activatedRoute.snapshot.queryParams;

    if (
      queryParams.hasOwnProperty('filter')
    ) {
      return {
        filter: queryParams['filter'],
      };
    }

    return null;
  }
}
